/**
 * PHASE 2: Network / Connectivity Layer
 * Global connectivity helpers for Chain29 offline sync engine
 */

let connectivitySubscribers = [];
let lastNetworkStatus = {
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
  lastCheckedAt: Date.now(),
  heartbeatOk: null,
  effectiveType: null,
  downlink: null
};

// Debounce timer for flapping connectivity
let connectivityDebounceTimer = null;
const DEBOUNCE_MS = 1000;

// Heartbeat config
const HEARTBEAT_ENDPOINT = '/api/health';
const HEARTBEAT_TIMEOUT = 5000;
let heartbeatInterval = null;

/**
 * Check if browser reports online
 */
export function isBrowserOnline() {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

/**
 * Get effective connection info (if available)
 */
export function getEffectiveConnectionInfo() {
  if (typeof navigator !== 'undefined' && 'connection' in navigator) {
    const conn = navigator.connection;
    return {
      effectiveType: conn.effectiveType, // 'slow-2g', '2g', '3g', '4g'
      downlink: conn.downlink, // Mbps
      rtt: conn.rtt, // ms
      saveData: conn.saveData
    };
  }
  return null;
}

/**
 * Get current network status
 */
export function getNetworkStatus() {
  return { ...lastNetworkStatus };
}

/**
 * Wait for online status
 * @param {number} timeoutMs - optional timeout
 * @returns {Promise<boolean>}
 */
export function waitForOnline(timeoutMs = 30000) {
  return new Promise((resolve) => {
    if (isBrowserOnline()) {
      resolve(true);
      return;
    }
    
    let timeoutId;
    const handler = () => {
      clearTimeout(timeoutId);
      window.removeEventListener('online', handler);
      resolve(true);
    };
    
    window.addEventListener('online', handler);
    
    if (timeoutMs > 0) {
      timeoutId = setTimeout(() => {
        window.removeEventListener('online', handler);
        resolve(false);
      }, timeoutMs);
    }
  });
}

/**
 * Subscribe to connectivity changes
 * @param {Function} callback - (status) => void
 * @returns {Function} unsubscribe function
 */
export function subscribeToConnectivity(callback) {
  connectivitySubscribers.push(callback);
  
  // Return unsubscribe function
  return () => {
    connectivitySubscribers = connectivitySubscribers.filter(cb => cb !== callback);
  };
}

/**
 * Notify all subscribers of connectivity change
 */
function notifySubscribers(status) {
  connectivitySubscribers.forEach(cb => {
    try {
      cb(status);
    } catch (e) {
      console.error('[Network] Subscriber error:', e);
    }
  });
}

/**
 * Update network status with debouncing
 */
function updateNetworkStatus(updates) {
  // Clear existing debounce timer
  if (connectivityDebounceTimer) {
    clearTimeout(connectivityDebounceTimer);
  }
  
  connectivityDebounceTimer = setTimeout(() => {
    const previousOnline = lastNetworkStatus.online;
    
    lastNetworkStatus = {
      ...lastNetworkStatus,
      ...updates,
      lastCheckedAt: Date.now()
    };
    
    // Only notify if online status actually changed
    if (previousOnline !== lastNetworkStatus.online) {
      console.log('[Network] Status changed:', lastNetworkStatus.online ? 'ONLINE' : 'OFFLINE');
      notifySubscribers(lastNetworkStatus);
    }
  }, DEBOUNCE_MS);
}

/**
 * Perform heartbeat check to verify backend reachability
 */
export async function performHeartbeat() {
  if (!isBrowserOnline()) {
    updateNetworkStatus({ online: false, heartbeatOk: false });
    return false;
  }
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HEARTBEAT_TIMEOUT);
    
    const response = await fetch(HEARTBEAT_ENDPOINT, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store'
    });
    
    clearTimeout(timeoutId);
    
    const heartbeatOk = response.ok;
    updateNetworkStatus({ online: heartbeatOk, heartbeatOk });
    return heartbeatOk;
  } catch (error) {
    // Network error or timeout - backend not reachable
    if (error.name !== 'AbortError') {
      console.log('[Network] Heartbeat failed:', error.message);
    }
    updateNetworkStatus({ online: isBrowserOnline(), heartbeatOk: false });
    return false;
  }
}

/**
 * Start periodic heartbeat checks
 * @param {number} intervalMs - check interval (default 30s)
 */
export function startHeartbeat(intervalMs = 30000) {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }
  
  // Initial check
  performHeartbeat();
  
  // Periodic checks
  heartbeatInterval = setInterval(() => {
    // Only check if browser thinks we're online
    if (isBrowserOnline()) {
      performHeartbeat();
    }
  }, intervalMs);
}

/**
 * Stop periodic heartbeat checks
 */
export function stopHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

/**
 * Initialize network monitoring
 */
export function initNetworkMonitoring() {
  if (typeof window === 'undefined') return;
  
  // Get initial connection info
  const connInfo = getEffectiveConnectionInfo();
  if (connInfo) {
    lastNetworkStatus.effectiveType = connInfo.effectiveType;
    lastNetworkStatus.downlink = connInfo.downlink;
  }
  
  // Listen to browser online/offline events
  window.addEventListener('online', () => {
    console.log('[Network] Browser reports: ONLINE');
    updateNetworkStatus({ online: true });
    // Verify with heartbeat
    performHeartbeat();
  });
  
  window.addEventListener('offline', () => {
    console.log('[Network] Browser reports: OFFLINE');
    updateNetworkStatus({ online: false, heartbeatOk: false });
  });
  
  // Listen to connection changes (if supported)
  if ('connection' in navigator) {
    navigator.connection.addEventListener('change', () => {
      const connInfo = getEffectiveConnectionInfo();
      if (connInfo) {
        lastNetworkStatus.effectiveType = connInfo.effectiveType;
        lastNetworkStatus.downlink = connInfo.downlink;
        console.log('[Network] Connection changed:', connInfo.effectiveType);
      }
    });
  }
  
  // Start heartbeat (less frequent to avoid spamming)
  startHeartbeat(60000);
  
  console.log('[Network] Monitoring initialized');
}

/**
 * Check if we should attempt network operations
 * More conservative than just checking navigator.onLine
 */
export function shouldAttemptNetworkOperation() {
  const status = getNetworkStatus();
  
  // If we know heartbeat failed recently, don't attempt
  if (status.heartbeatOk === false && 
      Date.now() - status.lastCheckedAt < 10000) {
    return false;
  }
  
  return status.online;
}

export default {
  isBrowserOnline,
  getEffectiveConnectionInfo,
  getNetworkStatus,
  waitForOnline,
  subscribeToConnectivity,
  performHeartbeat,
  startHeartbeat,
  stopHeartbeat,
  initNetworkMonitoring,
  shouldAttemptNetworkOperation
};
